<script>
import axios from 'axios';
import Layout from '../../layouts/main';
import PageHeader from '@/components/page-header';

export default {
  page: { title: 'WhatsApp Padok' },
  components: { Layout, PageHeader },
  data() {
    return {
      title: 'WhatsApp Padok',
      items: [{ text: 'Atendimento' }, { text: 'WhatsApp', active: true }],
      account: { status: 'disconnected' },
      serviceAvailable: true,
      conversations: [],
      selected: null,
      messages: [],
      messagesLoading: false,
      conversationLoadToken: 0,
      message: '',
      search: '',
      onlyUnread: false,
      loading: false,
      sending: false,
      statusLoading: false,
      profilesLoading: false,
      syncTimer: null,
      realtimeConnected: false,
      mediaUrls: {},
      newChat: { phone: '', name: '' },
      structured: { mode: null, latitude: '', longitude: '', locationName: '', locationAddress: '', contactName: '', contactPhone: '' },
    };
  },
  computed: {
    connected() { return this.account.status === 'connected'; },
    statusLabel() {
      const labels = { connected: 'Conectado', connecting: 'Conectando', qr_required: 'Aguardando leitura do QR Code', disconnected: 'Desconectado' };
      return labels[this.account.status] || 'Desconectado';
    },
    statusVariant() {
      return this.connected ? 'success' : this.account.status === 'qr_required' ? 'warning' : 'secondary';
    },
  },
  created() {
    this.user = JSON.parse(localStorage.getItem('user') || '{}');
    this.realtimeStopped = false;
    this.realtimeSocket = null;
    this.realtimeTimer = null;
    this.refreshTimer = null;
    Promise.all([this.loadStatus(), this.loadConversations()]).then(() => this.openBillingDraft()).finally(() => this.connectRealtime());
    this.syncTimer = setInterval(() => {
      this.loadStatus(true);
      this.loadConversations(true);
      if (this.selected) this.loadMessages(this.selected, true);
    }, 30000);
  },
  beforeDestroy() {
    this.realtimeStopped = true;
    clearInterval(this.syncTimer);
    clearTimeout(this.realtimeTimer);
    clearTimeout(this.refreshTimer);
    if (this.realtimeSocket) this.realtimeSocket.close();
    Object.values(this.mediaUrls).forEach(url => URL.revokeObjectURL(url));
  },
  methods: {
    headers() { return { Accept: 'application/json', Authorization: `Bearer ${this.user.access_token}` }; },
    endpoint(path) { return `${process.env.VUE_APP_API}api/v1/admin/whatsapp${path}`; },
    async openBillingDraft() {
      const raw = sessionStorage.getItem('padok.whatsapp.draft');
      if (!raw) return;
      sessionStorage.removeItem('padok.whatsapp.draft');
      try {
        const draft = JSON.parse(raw);
        if (!draft.phone || Date.now() - Number(draft.created_at || 0) > 5 * 60 * 1000) return;
        const { data } = await axios.post(this.endpoint('/conversations'), {
          phone: draft.phone,
          contact_name: draft.name || null,
        }, { headers: this.headers() });
        await this.loadConversations(true);
        await this.selectConversation(data.data);
        this.message = draft.message || '';
        this.$nextTick(() => {
          if (this.$refs.composer) this.$refs.composer.focus();
        });
      } catch (error) {
        this.alertError(error, 'Não foi possível abrir a conversa do comprador.');
      }
    },
    async connectRealtime() {
      if (this.realtimeStopped || (this.realtimeSocket && [WebSocket.OPEN, WebSocket.CONNECTING].includes(this.realtimeSocket.readyState))) return;
      try {
        const { data } = await axios.get(this.endpoint('/socket-token'), { headers: this.headers() });
        const config = data.data;
        const separator = config.url.includes('?') ? '&' : '?';
        const socket = new WebSocket(`${config.url}${separator}token=${encodeURIComponent(config.token)}`);
        this.realtimeSocket = socket;
        socket.onopen = () => { this.realtimeConnected = true; };
        socket.onmessage = event => {
          try {
            const payload = JSON.parse(event.data);
            if (payload.event !== 'ready') this.scheduleRealtimeRefresh(payload.event);
          } catch (_) { /* ignora somente eventos inválidos */ }
        };
        socket.onerror = () => socket.close();
        socket.onclose = () => {
          this.realtimeConnected = false;
          if (!this.realtimeStopped) this.realtimeTimer = setTimeout(() => this.connectRealtime(), 3000);
        };
      } catch (_) {
        this.realtimeConnected = false;
        if (!this.realtimeStopped) this.realtimeTimer = setTimeout(() => this.connectRealtime(), 5000);
      }
    },
    scheduleRealtimeRefresh(eventName) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(async () => {
        if (eventName === 'connection') await this.loadStatus(true);
        await this.loadConversations(true);
        if (this.selected) await this.loadMessages(this.selected, true);
      }, 250);
    },
    async loadStatus(silent = false) {
      if (!silent) this.statusLoading = true;
      try {
        const { data } = await axios.get(this.endpoint('/status'), { headers: this.headers() });
        this.account = data.data.account || this.account;
        this.serviceAvailable = data.data.service_available;
      } catch (error) {
        this.serviceAvailable = false;
      } finally { this.statusLoading = false; }
    },
    async connect() {
      this.statusLoading = true;
      try {
        const { data } = await axios.post(this.endpoint('/connect'), {}, { headers: this.headers() });
        this.account = data.data.account;
      } catch (error) { this.alertError(error, 'Não foi possível iniciar o WhatsApp.'); }
      finally { this.statusLoading = false; }
    },
    async disconnect() {
      const confirm = await this.$swal({ title: 'Desconectar o WhatsApp?', text: 'Será necessário ler um novo QR Code para conectar novamente.', icon: 'warning', showCancelButton: true, confirmButtonText: 'Desconectar', cancelButtonText: 'Cancelar' });
      if (!confirm.isConfirmed) return;
      try {
        const { data } = await axios.delete(this.endpoint('/disconnect'), { headers: this.headers() });
        this.account = data.data.account;
      } catch (error) { this.alertError(error, 'Não foi possível desconectar.'); }
    },
    async loadConversations(silent = false) {
      if (!silent) this.loading = true;
      try {
        const { data } = await axios.get(this.endpoint('/conversations'), { headers: this.headers(), params: { search: this.search, unread: this.onlyUnread ? 1 : 0 } });
        this.conversations = data.data.data || [];
        if (this.selected) {
          const updated = this.conversations.find(item => item.id === this.selected.id);
          if (updated) this.selected = updated;
        }
      } catch (error) { if (!silent) this.alertError(error, 'Não foi possível carregar as conversas.'); }
      finally { this.loading = false; }
    },
    async selectConversation(conversation) {
      const token = ++this.conversationLoadToken;
      this.selected = conversation;
      this.messages = [];
      this.messagesLoading = true;
      try {
        await this.loadMessages(conversation, false, token);
      } finally {
        if (token === this.conversationLoadToken) this.messagesLoading = false;
      }
    },
    async loadMessages(conversation, silent = false, selectionToken = null) {
      try {
        const { data } = await axios.get(this.endpoint(`/conversations/${conversation.id}/messages`), { headers: this.headers() });
        if (selectionToken !== null && selectionToken !== this.conversationLoadToken) return;
        if (!this.selected || this.selected.id !== conversation.id) return;
        this.messages = (data.data.messages.data || []).slice().reverse();
        await this.loadMedia(this.messages);
        this.$nextTick(() => {
          const body = this.$refs.messages;
          if (body && (!silent || body.scrollHeight - body.scrollTop - body.clientHeight < 160)) body.scrollTop = body.scrollHeight;
        });
      } catch (error) { if (!silent) this.alertError(error, 'Não foi possível abrir a conversa.'); }
    },
    async loadMedia(messages) {
      await Promise.all(messages.filter(item => item.media_path && !this.mediaUrls[item.id]).map(async item => {
        try {
          const response = await axios.get(this.endpoint(`/messages/${item.id}/media`), { headers: this.headers(), responseType: 'blob' });
          this.$set(this.mediaUrls, item.id, URL.createObjectURL(response.data));
        } catch (_) { /* mantém a conversa disponível se uma mídia antiga não existir */ }
      }));
    },
    async createConversation() {
      if (!this.newChat.phone) return;
      try {
        const { data } = await axios.post(this.endpoint('/conversations'), { phone: this.newChat.phone, contact_name: this.newChat.name || null }, { headers: this.headers() });
        this.$bvModal.hide('new-whatsapp-chat');
        this.newChat = { phone: '', name: '' };
        await this.loadConversations();
        await this.selectConversation(data.data);
      } catch (error) { this.alertError(error, 'Confira o telefone informado.'); }
    },
    async refreshProfiles() {
      if (!this.connected || this.profilesLoading) return;
      this.profilesLoading = true;
      try {
        const { data } = await axios.post(this.endpoint('/profiles/refresh'), {}, { headers: this.headers() });
        await this.loadConversations(true);
        this.$swal('Fotos atualizadas', `${data.data.updated} foto(s) de perfil disponível(is).`, 'success');
      } catch (error) { this.alertError(error, 'Não foi possível atualizar as fotos agora.'); }
      finally { this.profilesLoading = false; }
    },
    async sendText() {
      if (!this.message.trim()) return;
      const text = this.message;
      this.message = '';
      await this.sendPayload({ type: 'text', message: text });
    },
    chooseFile() { this.$refs.file.click(); },
    async fileSelected(event) {
      const file = event.target.files[0];
      event.target.value = '';
      if (!file) return;
      let type = 'document';
      if (file.type.startsWith('image/')) type = file.type === 'image/webp' ? 'sticker' : 'image';
      else if (file.type.startsWith('video/')) type = 'video';
      else if (file.type.startsWith('audio/')) type = 'audio';
      await this.sendPayload({ type, message: this.message || '', file });
      this.message = '';
    },
    openStructured(mode) {
      this.structured = { mode, latitude: '', longitude: '', locationName: '', locationAddress: '', contactName: '', contactPhone: '' };
      this.$bvModal.show('structured-whatsapp-message');
    },
    async sendStructured() {
      const form = this.structured.mode === 'location'
        ? { type: 'location', latitude: this.structured.latitude, longitude: this.structured.longitude, location_name: this.structured.locationName, location_address: this.structured.locationAddress }
        : { type: 'contact', contact_name: this.structured.contactName, contact_phone: this.structured.contactPhone };
      await this.sendPayload(form);
      this.$bvModal.hide('structured-whatsapp-message');
    },
    async react(item, emoji) {
      if (!item.external_message_id) return;
      await this.sendPayload({ type: 'reaction', target_external_id: item.external_message_id, target_from_me: item.direction === 'outbound', emoji });
    },
    async sendPayload(payload) {
      if (!this.selected || !this.connected || this.sending) return;
      this.sending = true;
      const form = new FormData();
      Object.keys(payload).forEach(key => {
        if (payload[key] !== null && payload[key] !== undefined) form.append(key, payload[key]);
      });
      try {
        await axios.post(this.endpoint(`/conversations/${this.selected.id}/messages`), form, { headers: { ...this.headers(), 'Content-Type': 'multipart/form-data' } });
        await this.loadMessages(this.selected);
        await this.loadConversations(true);
      } catch (error) { this.alertError(error, 'Não foi possível enviar a mensagem.'); }
      finally { this.sending = false; }
    },
    async archiveConversation() {
      if (!this.selected) return;
      try {
        await axios.put(this.endpoint(`/conversations/${this.selected.id}/archive`), { archived: true }, { headers: this.headers() });
        this.selected = null;
        this.messages = [];
        await this.loadConversations();
      } catch (error) { this.alertError(error, 'Não foi possível arquivar.'); }
    },
    alertError(error, fallback) {
      this.$swal('Atenção', error.response && error.response.data && error.response.data.message ? error.response.data.message : fallback, 'error');
    },
    displayName(item) { return item.contact_name || this.formatPhone(item.phone); },
    formatPhone(value) {
      const phone = String(value || '').replace(/\D/g, '');
      if (phone.startsWith('55') && phone.length >= 12) return `+55 (${phone.slice(2,4)}) ${phone.slice(4, phone.length - 4)}-${phone.slice(-4)}`;
      return `+${phone}`;
    },
    time(value) { return value ? this.$moment(value).format('DD/MM/YYYY HH:mm') : ''; },
    messageLabel(item) {
      const labels = { image: 'Imagem', video: 'Vídeo', audio: 'Áudio', document: 'Documento', sticker: 'Figurinha', location: 'Localização', contact: 'Contato', reaction: 'Reação' };
      const label = labels[item.type];
      if (!label) return item.body || 'Mensagem';
      return item.body && !['audio', 'sticker'].includes(item.type) ? `${label} · ${item.body}` : label;
    },
    mediaUnavailable(item) {
      if (!['image', 'video', 'audio', 'document', 'sticker'].includes(item.type) || this.mediaUrls[item.id]) return '';
      return item.metadata && item.metadata.mediaError === 'too_large'
        ? 'Arquivo maior que o limite de 20 MB'
        : 'Mídia ainda não disponível';
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="whatsapp-shell card">
      <div class="whatsapp-toolbar">
        <div>
          <strong><i class="ri-whatsapp-line mr-2"></i>WhatsApp Padok</strong>
          <b-badge class="ml-2" :variant="statusVariant">{{ statusLabel }}</b-badge>
          <small class="ml-2" :class="realtimeConnected ? 'text-success' : 'text-muted'"><i class="ri-flashlight-line"></i> {{ realtimeConnected ? 'Tempo real' : 'Reconectando' }}</small>
          <small v-if="connected && account.phone" class="text-muted ml-2">{{ formatPhone(account.phone) }}</small>
        </div>
        <div>
          <b-button v-if="connected" size="sm" variant="outline-secondary" class="mr-2" :disabled="profilesLoading" @click="refreshProfiles">
            <i class="ri-user-smile-line mr-1"></i> {{ profilesLoading ? 'Atualizando...' : 'Atualizar fotos' }}
          </b-button>
          <b-button v-if="!connected" size="sm" variant="success" :disabled="statusLoading || !serviceAvailable" @click="connect">
            <i class="ri-qr-code-line mr-1"></i> Conectar
          </b-button>
          <b-button v-else size="sm" variant="outline-danger" @click="disconnect">Desconectar</b-button>
        </div>
      </div>

      <b-alert v-if="!serviceAvailable" show variant="warning" class="m-3 mb-0">
        O serviço local do WhatsApp não está ativo. As conversas continuam salvas, mas novos envios ficam indisponíveis.
      </b-alert>
      <div v-if="account.status === 'qr_required' && account.qr_code" class="qr-panel">
        <img :src="account.qr_code" alt="QR Code para conectar o WhatsApp" />
        <div><strong>Leia o QR Code no WhatsApp</strong><br><span>Abra Aparelhos conectados no celular e escaneie este código.</span></div>
      </div>

      <div class="whatsapp-grid">
        <aside class="conversation-panel">
          <div class="conversation-tools">
            <div class="input-group input-group-sm">
              <input v-model="search" @keyup.enter="loadConversations()" class="form-control" placeholder="Buscar nome ou telefone" />
              <div class="input-group-append"><button class="btn btn-light" @click="loadConversations()"><i class="ri-search-line"></i></button></div>
            </div>
            <div class="d-flex align-items-center justify-content-between mt-2">
              <b-form-checkbox v-model="onlyUnread" @change="loadConversations()" size="sm">Somente não lidas</b-form-checkbox>
              <b-button v-b-modal.new-whatsapp-chat size="sm" variant="primary"><i class="ri-chat-new-line"></i> Nova</b-button>
            </div>
          </div>
          <div class="conversation-list">
            <button v-for="item in conversations" :key="item.id" class="conversation-item" :class="{ active: selected && selected.id === item.id }" @click="selectConversation(item)">
              <span class="avatar">
                <img v-if="item.contact_photo_url" :src="item.contact_photo_url" :alt="displayName(item)" referrerpolicy="no-referrer" />
                <template v-else>{{ displayName(item).charAt(0).toUpperCase() }}</template>
              </span>
              <span class="conversation-copy">
                <span class="d-flex justify-content-between"><strong>{{ displayName(item) }}</strong><small>{{ item.last_message_at ? $moment(item.last_message_at).format('DD/MM HH:mm') : '' }}</small></span>
                <span class="preview">{{ item.last_message ? messageLabel(item.last_message) : formatPhone(item.phone) }}</span>
              </span>
              <b-badge v-if="item.unread_count" pill variant="success">{{ item.unread_count }}</b-badge>
            </button>
            <div v-if="!loading && !conversations.length" class="empty-list">Nenhuma conversa encontrada.</div>
          </div>
        </aside>

        <section v-if="selected" class="chat-panel">
          <header class="chat-header">
            <div class="chat-contact">
              <span class="avatar chat-avatar">
                <img v-if="selected.contact_photo_url" :src="selected.contact_photo_url" :alt="displayName(selected)" referrerpolicy="no-referrer" />
                <template v-else>{{ displayName(selected).charAt(0).toUpperCase() }}</template>
              </span>
              <span><strong>{{ displayName(selected) }}</strong><br><small>{{ formatPhone(selected.phone) }}</small></span>
            </div>
            <button class="btn btn-sm btn-light" title="Arquivar conversa" :disabled="messagesLoading" @click="archiveConversation"><i class="ri-archive-line"></i></button>
          </header>
          <div ref="messages" class="message-list" :class="{ 'is-loading': messagesLoading }">
            <div v-if="messagesLoading" class="messages-loading" role="status" aria-live="polite">
              <b-spinner variant="success" label="Carregando conversa"></b-spinner>
              <span>Carregando conversa...</span>
            </div>
            <div v-else v-for="item in messages" :key="item.id" class="message-row" :class="item.direction">
              <div class="message-bubble">
                <div v-if="item.metadata && item.metadata.source === 'billing_notification'" class="billing-notification-label">
                  <i class="ri-bill-line"></i> Notificação automática de cobrança
                </div>
                <img v-if="['image','sticker'].includes(item.type) && mediaUrls[item.id]" :src="mediaUrls[item.id]" class="message-image" />
                <video v-else-if="item.type === 'video' && mediaUrls[item.id]" :src="mediaUrls[item.id]" controls class="message-video"></video>
                <audio v-else-if="item.type === 'audio' && mediaUrls[item.id]" :src="mediaUrls[item.id]" controls></audio>
                <a v-else-if="item.type === 'document' && mediaUrls[item.id]" :href="mediaUrls[item.id]" :download="item.file_name || 'documento'" class="document-link"><i class="ri-file-text-line"></i> {{ item.file_name || 'Abrir documento' }}</a>
                <div v-else-if="mediaUnavailable(item)" class="media-unavailable"><i class="ri-error-warning-line"></i> {{ mediaUnavailable(item) }}</div>
                <div v-if="item.type === 'location'" class="structured-message"><i class="ri-map-pin-line"></i> {{ item.body || 'Localização compartilhada' }}</div>
                <div v-else-if="item.type === 'contact'" class="structured-message"><i class="ri-contacts-line"></i> {{ item.body || 'Contato compartilhado' }}</div>
                <div v-if="item.body && !['reaction','audio','sticker','document'].includes(item.type)" class="message-text">{{ item.body }}</div>
                <div v-if="item.type === 'reaction'" class="reaction-message">{{ item.body }}</div>
                <div class="message-meta">
                  <span>{{ time(item.provider_timestamp || item.created_at) }}</span>
                  <span v-if="item.direction === 'outbound'" class="ml-2" :title="item.status">
                    <i v-if="item.status === 'failed'" class="ri-error-warning-line text-danger"></i>
                    <i v-else-if="item.status === 'read'" class="ri-check-double-line text-primary"></i>
                    <i v-else-if="item.status === 'delivered'" class="ri-check-double-line"></i>
                    <i v-else class="ri-check-line"></i>
                  </span>
                  <button v-if="item.external_message_id" class="reaction-button" @click="react(item, '👍')">👍</button>
                </div>
              </div>
            </div>
          </div>
          <footer class="composer">
            <input ref="file" type="file" class="d-none" @change="fileSelected" />
            <button class="btn btn-light" :disabled="!connected || messagesLoading" title="Anexar arquivo" @click="chooseFile"><i class="ri-attachment-2"></i></button>
            <button class="btn btn-light" :disabled="!connected || messagesLoading" title="Enviar localização" @click="openStructured('location')"><i class="ri-map-pin-line"></i></button>
            <button class="btn btn-light" :disabled="!connected || messagesLoading" title="Enviar contato" @click="openStructured('contact')"><i class="ri-contacts-line"></i></button>
            <textarea ref="composer" v-model="message" rows="1" :disabled="!connected || messagesLoading" class="form-control" placeholder="Digite uma mensagem" @keydown.enter.exact.prevent="sendText"></textarea>
            <button class="btn btn-success" :disabled="!connected || messagesLoading || sending || !message.trim()" @click="sendText"><i class="ri-send-plane-2-line"></i></button>
          </footer>
        </section>
        <section v-else class="chat-empty">
          <i class="ri-whatsapp-line"></i>
          <h4>Conversas da Padok</h4>
          <p>Selecione uma conversa ou inicie uma nova.</p>
        </section>
      </div>
    </div>

    <b-modal id="new-whatsapp-chat" title="Nova conversa" ok-title="Iniciar conversa" cancel-title="Cancelar" @ok.prevent="createConversation">
      <b-form-group label="Telefone com DDD"><b-form-input v-model="newChat.phone" placeholder="61 99999-9999" /></b-form-group>
      <b-form-group label="Nome do contato (opcional)"><b-form-input v-model="newChat.name" /></b-form-group>
    </b-modal>
    <b-modal id="structured-whatsapp-message" :title="structured.mode === 'location' ? 'Enviar localização' : 'Enviar contato'" ok-title="Enviar" cancel-title="Cancelar" @ok.prevent="sendStructured">
      <template v-if="structured.mode === 'location'">
        <div class="row"><b-form-group class="col" label="Latitude"><b-form-input v-model="structured.latitude" /></b-form-group><b-form-group class="col" label="Longitude"><b-form-input v-model="structured.longitude" /></b-form-group></div>
        <b-form-group label="Nome do local"><b-form-input v-model="structured.locationName" /></b-form-group>
        <b-form-group label="Endereço"><b-form-input v-model="structured.locationAddress" /></b-form-group>
      </template>
      <template v-else>
        <b-form-group label="Nome"><b-form-input v-model="structured.contactName" /></b-form-group>
        <b-form-group label="Telefone"><b-form-input v-model="structured.contactPhone" /></b-form-group>
      </template>
    </b-modal>
  </Layout>
</template>

<style scoped>
.whatsapp-shell{overflow:hidden;min-height:700px}.whatsapp-toolbar{height:64px;padding:0 18px;display:flex;align-items:center;justify-content:space-between;border-bottom:1px solid #e9ecef}.qr-panel{display:flex;align-items:center;justify-content:center;gap:22px;padding:18px;background:#fff8dd;border-bottom:1px solid #f1df9d}.qr-panel img{width:180px;height:180px;border-radius:8px}.whatsapp-grid{display:grid;grid-template-columns:340px minmax(0,1fr);min-height:635px}.conversation-panel{border-right:1px solid #e9ecef;background:#fff}.conversation-tools{padding:14px;border-bottom:1px solid #eef1f4}.conversation-list{max-height:570px;overflow-y:auto}.conversation-item{width:100%;border:0;border-bottom:1px solid #f1f3f5;background:#fff;display:flex;align-items:center;gap:10px;padding:13px;text-align:left;color:#495057}.conversation-item:hover,.conversation-item.active{background:#eefaf4}.avatar{width:42px;height:42px;border-radius:50%;display:grid;place-items:center;background:#d9f5e6;color:#087f5b;font-weight:700;flex:none;overflow:hidden}.avatar img{width:100%;height:100%;object-fit:cover}.conversation-copy{min-width:0;flex:1}.conversation-copy strong,.preview{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.conversation-copy small{font-size:10px;color:#868e96;font-weight:400}.preview{font-size:12px;color:#868e96;margin-top:4px}.empty-list{padding:35px 20px;text-align:center;color:#868e96}.chat-panel{display:flex;flex-direction:column;min-width:0;background:#efeae2}.chat-header{height:60px;background:#fff;display:flex;align-items:center;justify-content:space-between;padding:0 18px;border-bottom:1px solid #e9ecef}.chat-contact{display:flex;align-items:center;gap:10px}.chat-avatar{width:38px;height:38px}.message-list{height:510px;overflow-y:auto;padding:20px 5%;background-color:#efeae2;background-image:radial-gradient(#d9d2c5 0.7px,transparent 0.7px);background-size:12px 12px}.message-list.is-loading{display:flex;align-items:center;justify-content:center}.messages-loading{display:flex;flex-direction:column;align-items:center;gap:12px;padding:28px;color:#66756c;font-weight:600}.message-row{display:flex;margin:7px 0}.message-row.inbound{justify-content:flex-start}.message-row.outbound{justify-content:flex-end}.message-bubble{max-width:72%;padding:9px 11px 6px;border-radius:9px;background:#fff;box-shadow:0 1px 1px rgba(0,0,0,.08);overflow:hidden}.message-row.outbound .message-bubble{background:#d9fdd3}.message-image,.message-video{display:block;max-width:100%;max-height:330px;border-radius:6px;margin-bottom:7px}.message-bubble audio{max-width:100%}.document-link,.structured-message,.media-unavailable{display:block;padding:10px;border-radius:6px;background:rgba(255,255,255,.55);margin-bottom:5px}.media-unavailable{color:#ad6800}.message-text{white-space:pre-wrap;word-break:break-word}.message-meta{font-size:10px;color:#768079;text-align:right;margin-top:4px}.reaction-button{border:0;background:transparent;padding:0 0 0 8px;opacity:.55}.reaction-button:hover{opacity:1}.composer{min-height:65px;background:#f7f7f7;display:flex;align-items:center;gap:7px;padding:9px 12px;border-top:1px solid #dee2e6}.composer textarea{resize:none;max-height:90px}.chat-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;background:#f8f9fa;color:#868e96;border-bottom:5px solid #25d366}.chat-empty i{font-size:72px;color:#25d366}.chat-empty h4{color:#495057}.reaction-message{font-size:24px}@media(max-width:900px){.whatsapp-grid{grid-template-columns:1fr}.conversation-panel{border-right:0}.chat-panel,.chat-empty{min-height:600px}.conversation-list{max-height:260px}.qr-panel{flex-direction:column;text-align:center}.message-bubble{max-width:88%}}
.billing-notification-label{font-size:10px;font-weight:600;color:#087f5b;border-bottom:1px solid rgba(8,127,91,.18);padding-bottom:5px;margin-bottom:6px}
</style>
